/**
 * useMarkEingangRead - opening an Eingang marks it read for the calling viewer (epic B2).
 *
 * POST /posteingang/:commId/read is idempotent per (tenant, comm, viewer), so this fires
 * once per mounted Eingang and needs no "already read?" check. The read mark is per
 * viewer, never global: a colleague opening the same mail still has it unread.
 *
 * Fire-and-forget on purpose. A failed read mark must not surface anything - the broker
 * asked to open a mail, not to mark it - so it only skips the invalidation and the row
 * stays unread until the next open.
 *
 * mock mode is a no-op: the fixture has no per-viewer state and the screen must stay
 * network-free.
 *
 * [Source: docs/eingangsverarbeitung-epic.md §3 WS-B B2] (mark-read on open)
 * [Source: apps/web/src/features/posteingang/hooks/usePosteingang.ts] (the same query keys)
 */

import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../lib/api-client';
import { isLiveData } from '../../../lib/data-mode';
import { qk } from '../../../lib/query-keys';

export function useMarkEingangRead(commId: string | null): void {
  const qc = useQueryClient();
  const live = isLiveData();

  useEffect(() => {
    if (!live || !commId) return;
    let cancelled = false;

    void apiClient.posteingang
      .read(commId)
      .then(() => {
        // Unmounted before the POST landed: the mark is written either way, but this
        // component no longer owns the refetch.
        if (cancelled) return;
        // The list row's `unread` and the nav badge both change with it.
        void qc.invalidateQueries({ queryKey: qk.posteingang() });
        void qc.invalidateQueries({ queryKey: qk.posteingangCounts('live') });
      })
      .catch(() => {
        // Deliberately silent - see the header.
      });

    return () => {
      cancelled = true;
    };
  }, [commId, live, qc]);
}
